import React, { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Divider, Flex, FlexSpan, Icon, IconButton, IconToggleButton } from "@evergis/ui";
import { area as calcArea, length as calcLength } from "@evergis/sgis/es/geotools";
import { useMapContext } from "@evergis/react";
import { toWgs84 } from "@turf/turf";
import { Coordinates } from "@evergis/sgis/es/baseTypes";

import {
  BackCard,
  Card,
  CardsContainer,
  FeatureCardButtonsContainer,
  FeatureTools,
  FeatureToolsDividerContainer,
  Header,
  HeaderFrontView,
  HeaderTitle,
  LayerDescription,
  Main,
  StatCount,
  TitleContainer,
} from "../../styled";

import { LayerPickMenu } from "../LayerPickMenu";
import { FieldValue } from "./FieldValue";
import { MainContentContainer } from "./styled";

import { getBottomHeight, getBottomState } from "../../../../ducks/layout/selectors";
import { useFeatureCard } from "../../hooks/useFeatureCard";
import { useGeometrySelection } from "../../../../hooks/geometrySelection/useGeometrySelection";
import { formatArea, formatLength } from "../../../../react-sgis/utils";

import { BottomState } from "../../../../ducks/layout/layout";
import { NoFeatureCardProps } from "./types";
import { GeometrySelectionTypes } from "../../../Map/components/GeometrySelectionTools/types";

export const NoFeatureCard: FC<NoFeatureCardProps> = ({ description }) => {
  const { t } = useTranslation("featureCard");
  const { map } = useMapContext();
  const bottomState: BottomState = useSelector(getBottomState);
  const bottomHeight: number = useSelector(getBottomHeight);
  const [, { closeCard }] = useFeatureCard();
  const [{ history, type }] = useGeometrySelection();

  const renderMultipleObjectsLabel = useCallback(() => {
    switch (type) {
      case GeometrySelectionTypes.Point:
        return t("pointsCount");
      case GeometrySelectionTypes.Circle:
      case GeometrySelectionTypes.Rect:
      case GeometrySelectionTypes.Polygon:
        return t("polygonsCount");
      case GeometrySelectionTypes.Line:
      case GeometrySelectionTypes.Route:
        return t("linesCount");
      default:
        return "";
    }
  }, [type, t]);

  const renderSingleFeatureFieldValue = useCallback(() => {
    switch (type) {
      case GeometrySelectionTypes.Point:
        return (
          <FieldValue
            field={t("coordinates")}
            value={toWgs84(history.features[history.step][0]._position)
              .reverse()
              .map(item => item.toFixed(6))
              .join(", ")}
          />
        );
      case GeometrySelectionTypes.Circle:
      case GeometrySelectionTypes.Rect:
      case GeometrySelectionTypes.Polygon:
        return (
          <Flex style={{ gap: "1rem", justifyContent: "space-between" }}>
            <FlexSpan style={{ flexGrow: 1 }}>
              <FieldValue
                field={t("area")}
                value={`${formatArea(
                  history.features[history.step].reduce(
                    (acc, curr) => {
                      return [
                        acc[0] + calcArea(curr._rings as Coordinates[][], map.crs),
                        acc[1] + calcLength(curr._rings as Coordinates[][], map.crs, true),
                      ];
                    },
                    [0, 0],
                  )[0],
                )}`}
              />
            </FlexSpan>
            <FlexSpan style={{ flexGrow: 1 }}>
              <FieldValue
                field={t("perimeter")}
                value={`${formatLength(
                  history.features[history.step].reduce(
                    (acc, curr) => {
                      return [
                        acc[0] + calcArea(curr._rings as Coordinates[][], map.crs),
                        acc[1] + calcLength(curr._rings as Coordinates[][], map.crs, true),
                      ];
                    },
                    [0, 0],
                  )[1],
                )}`}
              />
            </FlexSpan>
          </Flex>
        );
      case GeometrySelectionTypes.Line:
      case GeometrySelectionTypes.Route:
        return (
          <FieldValue
            field={t("length")}
            value={formatLength(calcLength(history.features[history.step][0]._rings as Coordinates[][], map.crs, true))}
          />
        );
      default:
        return null;
    }
  }, [type, history, map, t]);

  return (
    <CardsContainer>
      <BackCard>
        <StatCount>{t("objects")}: 0</StatCount>
        <FeatureTools>
          <IconToggleButton disabled>
            <Icon kind="stats" />
          </IconToggleButton>
          <IconToggleButton disabled>
            <Icon kind="preview" />
          </IconToggleButton>
          <FeatureToolsDividerContainer>
            <Divider vertical />
          </FeatureToolsDividerContainer>
          <LayerPickMenu />
        </FeatureTools>
      </BackCard>
      <Card bottomState={bottomState} bottomHeight={bottomHeight}>
        <Header $isRow>
          <HeaderFrontView>
            <TitleContainer style={{ paddingLeft: "0.5rem" }}>
              <HeaderTitle>{t("noObjectFound")}</HeaderTitle>
              {description && <LayerDescription>{description}</LayerDescription>}
            </TitleContainer>
            <FeatureCardButtonsContainer>
              <IconButton kind={"close"} onClick={closeCard} />
            </FeatureCardButtonsContainer>
          </HeaderFrontView>
        </Header>
        {history.features.length > 0 && history.step > -1 && (
          <Main padding="0.5rem 0.5rem 1rem 1.25rem">
            <MainContentContainer>
              <Flex style={{ justifyContent: "center", marginBottom: "1rem" }}>
                <Icon kind="points" disabled />
              </Flex>
              <Flex style={{ textAlign: "center" }}>{t("noFeatures")}</Flex>
              {history.features[history.step].length > 1 ? (
                <FieldValue field={renderMultipleObjectsLabel()} value={`${history.features[history.step].length}`} />
              ) : (
                renderSingleFeatureFieldValue()
              )}
            </MainContentContainer>
          </Main>
        )}
      </Card>
    </CardsContainer>
  );
};
